import { useEffect, useState, useRef, ChangeEvent } from "react";
import { useDispatch } from "react-redux";
import CalendarBackend from "../Calendar/utils/CalendarBackend";
import { setCalendars } from "../../store/features/cacheSlice";
import toast from "react-hot-toast";
import Datagrid from "../..//components/Datagrid/Datagrid";
import { BackendApi } from "../../utils/BackendApi";
import { SelectionChangedEvent, ColDef } from "ag-grid-community";
import "bootstrap/dist/css/bootstrap.min.css";
import common from "../..//utils/common";
import { ApiRoute } from "../..//utils/ApiRoute";
import { useWindowSize } from "../../hooks/useWindowSize";
import RecordSearchInput from "../..//components/Datagrid/RecordSearchInput";

import { AgGridReact } from "ag-grid-react";
import { SystemMsg } from "../../utils/SystemMsg";
import { ColorColumnCell } from "../../components/ColorColumnCell";
import TrashGridControl from "./TrashGridControl";
import "bootstrap/dist/css/bootstrap.css";
import type { IdMultiRequest } from "../../class/request.schema";
import { SearchRecordRequest } from "../../class/record.schema";
import { TrashGridType } from "../../class/frontend.schema";

type TrashCalendarGridProps = {
  toast: typeof toast;
  recordType: TrashGridType;
  setRecordTypeFunc: (type: TrashGridType) => void;
};

const TrashCalendarGrid = ({
  toast,
  recordType,
  setRecordTypeFunc,
}: TrashCalendarGridProps) => {
  const dispatch = useDispatch();
  const gridRef = useRef<AgGridReact>(null);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [rowData, setRowData] = useState<any[]>();
  const [rowLoaded, setRowLoaded] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(common?.pageSizeArray[1]);
  const [pageCount, setPageCount] = useState(0);
  const [paginationPage, setPaginationPage] = useState(0);

  //Search
  const [keyword, setKeyword] = useState("");
  const [searchStartDate, setSearchStartDate] = useState("");
  const [searchEndDate, setSearchEndDate] = useState("");

  const { height } = useWindowSize();

  const colorColumnRenderer = (params: any) => {
    const isHex = /^#([0-9A-F]{3}){1,2}$/i.test(params.value);
    const colorText = isHex
      ? params.value.toString().toUpperCase()
      : params.value.toString();
    return <ColorColumnCell color={params.value} text={colorText} />;
  };

  const colDefs = [
    { field: "id", width: 50 },
    { field: "name", headerName: "Calendar", flex: 2 },
    { field: "color", flex: 1, cellRenderer: colorColumnRenderer },
  ] as ColDef[];

  const getSearchParam = () => {
    const searchParam: SearchRecordRequest = {
      page: page > 0 ? page : 1,
      pageSize: pageSize,
    };
    if (
      !common.stringIsNullOrEmpty(keyword) &&
      keyword.toString().trim().length > 0
    ) {
      searchParam[`keyword`] = keyword;
    }
    return searchParam;
  };
  const getRowData = () => {
    //common.setDataGridLoading(gridRef, true);
    BackendApi.get(
      `${ApiRoute.calendar}/trash`,
      getSearchParam(),
      (resp) => {
        //common.setDataGridLoading(gridRef, false);
        const total =
          resp.data.total != null && !Number.isNaN(resp.data.total.toString())
            ? resp.data.total
            : 0;
        setPageCount(common.getPageCount(total, pageSize));
        if (Array.isArray(resp.data.data)) {
          setRowData(resp.data.data);
        } else {
          setRowData([]);
        }
        setRowLoaded(true);
      },
      (resp) => {
        // common.setDataGridLoading(gridRef, false);
        toast.error(SystemMsg.Record.failedToLoadRecord);
        console.log(resp);
        setRowLoaded(true);
      },
    );
  };

  const onSelectionChanged = (event: SelectionChangedEvent) => {
    if (
      !event ||
      !event.selectedNodes ||
      event.selectedNodes === null ||
      !Array.isArray(event.selectedNodes) ||
      event.selectedNodes.length === 0
    ) {
      setSelectedIds([]);
      return;
    }
    const selectedNode = event.selectedNodes;
    setSelectedIds(
      selectedNode
        .filter((x) => Number.isInteger(Number(x.data.id)))
        .map((x) => Number(x.data.id)),
    );
  };

  const RestoreRecords = () => {
    if (
      !selectedIds ||
      !Array.isArray(selectedIds) ||
      selectedIds.length === 0
    ) {
      toast.error(SystemMsg.GridUi.pleaseSelectRecords);
      return;
    }
    const request = { ids: selectedIds } as IdMultiRequest;
    BackendApi.put(
      `${ApiRoute.calendar}/restore`,
      request,
      (resp) => {
        const success = resp.status === 200 || resp.status === 202;
        if (!success) {
          toast.error(SystemMsg.GridUi.failedToRestoreXRecord(recordType));
          return;
        }
        toast.success(
          SystemMsg.GridUi.successfullyRestoredXRecord(
            selectedIds.length,
            recordType,
          ),
        );
        RefreshRedux();
        RemoveOnGird(selectedIds);
      },
      (resp) => {
        toast.error(SystemMsg.GridUi.failedToRestoreXRecord(recordType));
        console.log(resp);
      },
    );
  };

  const DeleteRecords = () => {
    if (
      !selectedIds ||
      !Array.isArray(selectedIds) ||
      selectedIds.length === 0
    ) {
      toast.error(SystemMsg.GridUi.pleaseSelectRecords);
      return;
    }
    const request = { ids: selectedIds } as IdMultiRequest;
    BackendApi.delete(
      `${ApiRoute.calendar}/delete`,
      request,
      (resp) => {
        const success = resp.status === 200 || resp.status === 202;
        if (!success) {
          toast.error(SystemMsg.GridUi.failedToDeleteXRecord(recordType));
          return;
        }
        toast.success(
          SystemMsg.GridUi.successfullyDeletedXRecord(
            selectedIds.length,
            recordType,
          ),
        );
        RefreshRedux();
        RemoveOnGird(selectedIds);
      },
      (resp) => {
        toast.error(SystemMsg.GridUi.failedToDeleteXRecord(recordType));
        console.log(resp);
      },
    );
  };

  const RefreshRedux = () => {
    CalendarBackend.getCalendarData((data) => {
      if (Array.isArray(data)) {
        dispatch(setCalendars(data));
      }
    });
  };

  const RemoveOnGird = (ids: number[]) => {
    const tempIdArray = Array.from(ids, (num) => num.toString());
    const removables = rowData?.filter((x) =>
      tempIdArray.includes(x.id.toString()),
    );
    gridRef?.current?.api.applyTransaction({ remove: removables ?? [] });
    const newRowData = rowData?.filter(
      (x) => !tempIdArray.includes(x.id.toString()),
    );
    if (newRowData !== undefined) {
      setRowData([...newRowData]);
    }
  };

  const topMenuElement = (
    <TrashGridControl
      onRestoreClick={RestoreRecords}
      onDeleteClick={DeleteRecords}
      recordType={recordType}
      setRecordTypeFunc={setRecordTypeFunc}
      disabled={
        !selectedIds || !Array.isArray(selectedIds) || selectedIds.length === 0
      }
    />
  );

  useEffect(() => {
    getRowData();
  }, []);

  useEffect(() => {
    setPage(1);
    setPaginationPage(0);
  }, [pageSize]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (rowLoaded) {
        getRowData();
      }
    }, common.DEBOUNCE_TIME);
    return () => clearTimeout(debounce);
  }, [keyword, searchStartDate, searchEndDate]);

  useEffect(() => {
    if (rowLoaded) {
      getRowData();
    }
  }, [page, pageSize]);

  const handlePageClick = (event: { selected: number }) => {
    if (!event || event?.selected == null || Number.isNaN(event.selected)) {
      return;
    }
    setPage(event.selected + 1);
    setPaginationPage(event.selected);
  };

  const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (
      !event ||
      !event?.target ||
      event?.target?.value == null ||
      Number.isInteger(event?.target?.value)
    ) {
      return;
    }
    setPageSize(Number(event.target.value));
  };

  const topLeftElement = (
    <RecordSearchInput searchValue={keyword} setSearchValueFunc={setKeyword} />
  );

  const getPageSizeArray = () => {
    const temp = [...common.pageSizeArray];
    temp.shift();
    return temp;
  };

  return (
    <>
      <Datagrid
        rowSelect="multiRow"
        gridRef={gridRef}
        rowData={rowData}
        colDefs={colDefs}
        onSelectionChanged={onSelectionChanged}
        topLeftElement={topLeftElement}
        topRightElement={topMenuElement}
        hasControls={true}
        hasPagination
        handlePageClick={handlePageClick}
        handlePageSizeChange={handlePageSizeChange}
        paginatePage={paginationPage}
        paginatePageCount={pageCount}
        paginatePageSizeArray={getPageSizeArray()}
        windowHeight={height}
        loading={!rowLoaded}
      />
    </>
  );
};

export default TrashCalendarGrid;
